import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { UserProfile, UserStats } from '../../core/models/user.models';
import { AdminUsersService } from '../../core/services/admin-users.service';

@Component({
  selector: 'app-user-details-page',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './user-details-page.component.html',
  styleUrl: './user-details-page.component.scss',
})
export class UserDetailsPageComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly usersService = inject(AdminUsersService);

  user: UserProfile | null = null;
  stats: UserStats | null = null;
  loading = true;
  errorMessage = '';

  async ngOnInit(): Promise<void> {
    const rawId = this.route.snapshot.paramMap.get('id');
    const userId = rawId ? Number(rawId) : NaN;
    if (!Number.isFinite(userId) || userId <= 0) {
      this.loading = false;
      this.errorMessage = 'Identifiant utilisateur invalide.';
      return;
    }

    this.loading = true;
    this.errorMessage = '';

    try {
      const user = await firstValueFrom(this.usersService.getById(userId));
      this.user = user;

      const memberId = user.member?.id;
      if (memberId) {
        try {
          this.stats = await firstValueFrom(this.usersService.getMemberStats(memberId));
        } catch {
          this.stats = null;
        }
      }
    } catch (error: unknown) {
      this.errorMessage = this.extractErrorMessage(error, 'Impossible de charger l’utilisateur.');
    } finally {
      this.loading = false;
    }
  }

  displayName(): string {
    const member = this.user?.member;
    return (
      member?.displayName ||
      `${member?.firstname ?? ''} ${member?.lastname ?? ''}`.trim() ||
      this.user?.email ||
      'Utilisateur'
    );
  }

  private extractErrorMessage(error: unknown, fallback: string): string {
    if (typeof error === 'string' && error.trim().length > 0) {
      return error;
    }

    if (error && typeof error === 'object') {
      const record = error as { error?: { message?: unknown }; message?: unknown };
      if (typeof record.error?.message === 'string' && record.error.message.trim().length > 0) {
        return record.error.message;
      }
      if (typeof record.message === 'string' && record.message.trim().length > 0) {
        return record.message;
      }
    }

    return fallback;
  }
}
